import {useGetAssignmentFormsQuery, useLazyGetFormQuery} from '../../../../api/services/formsService';
import React, {useEffect, useState} from 'react';
import {Box, Button, Divider, MenuItem, TextField} from '@mui/material';
import {availableComponents} from '../FormBuilder/FormBuilder';
import {determinePossibleOperations} from '../FormBuilder/ShowFieldWhen';
import SettingsSectionBase, {SettingsSection} from './SettingsSection';
import FORM_AUDIENCE_TYPE from '../../../../constants/FormAudienceType';
import EmailsInput from '../../../../shared-components/EmailsInput';
import {mergeArraysByKey} from '../../../../utility/mergeArraysByKey';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';

/* eslint-disable react/prop-types */

const triggerFormSettingsInitialState = {
    selectedForm: null,
    selectedFormSchema: null,
    relatedComponent: null,
    targetState: null,
    relatedFormEmailFieldId: null
};

/** Schema components that can hold an email (for "send trigger form to this field's value") */
const getEmailFieldsFromSchema = (schema) => {
    if (!Array.isArray(schema)) return [];
    return schema.filter(
        (c) =>
            c.type === 'previousSchoolEmail' ||
            (c.type === 'singleText' && c.inputType === 'email')
    );
};

const updateTrigger = (wizardMode, prev, update) => {
    if (wizardMode) {
        return {
            ...prev,
            ...update,
        };
    }
    return {
        ...prev,
        triggerFormSettings: {
            ...prev.triggerFormSettings,
            ...update,
        }
    };
};

const TriggerFormSettings = ({
                                 wizardMode = false,
                                 formId,
                                 audience,
                                 settings,
                                 externalUsers,
                                 onSettingsChange
                             }) => {

    const triggerFormSettings = wizardMode
        ? settings
        : settings.triggerFormSettings || {};

    const externalEmails = wizardMode
        ? settings.externalEmails || []
        : settings.externalUserEmailAssignments || [];

    const [originalSettings, setOriginalSettings] = useState({
        externalUserEmailAssignments: [],
        triggerFormSettings: {}
    });
    const {data: forms = []} = useGetAssignmentFormsQuery({
        formId,
        queryParams: {
            rootAudienceType: audience
        }
    });
    const [getForm] = useLazyGetFormQuery();

    const formsWithSelected = React.useMemo(() => {
        if (!triggerFormSettings.selectedForm) {
            return forms;
        }

        const isSelectedInList = forms.some(f => f.id === triggerFormSettings.selectedForm.id);
        if (isSelectedInList) return forms;

        return [
            {
                id: triggerFormSettings.selectedForm.id,
                name: triggerFormSettings.selectedForm.name
            },
            ...forms
        ];
    }, [forms, triggerFormSettings.selectedForm]);

    useEffect(() => {
        if (externalUsers) {
            onSettingsChange(prev => ({
                ...prev,
                externalUserEmailAssignments: externalUsers
            }));
            setOriginalSettings(prev => ({
                ...prev,
                externalUserEmailAssignments: externalUsers
            }));
        }
    }, [externalUsers]);

    const externalRecipientsReadOnly = !wizardMode &&
        audience === FORM_AUDIENCE_TYPE.externalUsers &&
        !!triggerFormSettings.relatedFormEmailFieldId;

    return (
        <>
            <SettingsSectionBase
                header={wizardMode ? undefined : 'Trigger'}
                headerIcon={wizardMode ? undefined : <BoltOutlinedIcon fontSize="small"/>}
                subHeader={
                    wizardMode ? undefined : 'Choose the form, question, and condition that will trigger this form'
                }
            >
                <SettingsSection>

                    <TextField
                        required
                        select
                        fullWidth
                        value={triggerFormSettings.selectedForm?.id || ''}
                        label="Form"
                        onChange={async e => {
                            const formId = e.target.value;
                            const response = await getForm(formId);
                            const form = response.data;

                            onSettingsChange(prev =>
                                updateTrigger(wizardMode, prev, {
                                    ...triggerFormSettingsInitialState,
                                    selectedForm: {
                                        id: form.id,
                                        name: form.name,
                                        schema: form.schema,
                                        audience: form.audience,
                                        assignmentType: form.assignmentType,
                                        accessLevel: form.accessLevel
                                    }
                                })
                            );
                        }}
                    >
                        {formsWithSelected?.map(({
                                                     id,
                                                     name
                                                 }) =>
                            <MenuItem key={id} value={id}>{name}</MenuItem>
                        )}
                    </TextField>

                    <TextField
                        select
                        value={triggerFormSettings.relatedComponent || ''}
                        disabled={!triggerFormSettings.selectedForm?.schema}
                        label="Question"
                        required
                        onChange={e =>
                            onSettingsChange(prev =>
                                updateTrigger(wizardMode, prev, {
                                    ...triggerFormSettings,
                                    relatedComponent: e.target.value
                                })
                            )
                        }
                    >
                        {triggerFormSettings.selectedForm?.schema
                            ?.filter(opt => availableComponents.includes(opt.type))
                            .map(opt => (
                                <MenuItem key={opt.id} value={opt}>
                                    {opt.label} - {opt.description}
                                </MenuItem>
                            ))}
                    </TextField>

                    <TextField
                        select
                        value={triggerFormSettings.targetState}
                        label="Equals"
                        required
                        disabled={!triggerFormSettings.relatedComponent}
                        onChange={e =>
                            onSettingsChange(prev =>
                                updateTrigger(wizardMode, prev, {
                                    targetState: e.target.value
                                })
                            )
                        }
                    >
                        {triggerFormSettings.relatedComponent &&
                            determinePossibleOperations(
                                triggerFormSettings.relatedComponent,
                                {checkboxOnlyChecked: true}
                            ).map(op => (
                                <MenuItem key={op.value} value={op.value}>
                                    {op.title}
                                </MenuItem>
                            ))}
                    </TextField>

                    {audience === FORM_AUDIENCE_TYPE.externalUsers &&
                        triggerFormSettings.selectedForm?.schema && (
                            <TextField
                                select
                                fullWidth
                                value={triggerFormSettings.relatedFormEmailFieldId || ''}
                                label="Email from field"
                                helperText="Optional. Pick a field from the trigger form that holds the recipient email. Leave empty to use the external user list."
                                onChange={e =>
                                    onSettingsChange(prev =>
                                        updateTrigger(wizardMode, prev, {
                                            relatedFormEmailFieldId:
                                                e.target.value || null
                                        })
                                    )
                                }
                            >
                                <MenuItem value="">None (use external user list)</MenuItem>
                                {getEmailFieldsFromSchema(
                                    triggerFormSettings.selectedForm.schema
                                ).map((c) => (
                                    <MenuItem key={c.id} value={c.id}>
                                        {c.description || c.label || c.id}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}

                    <Box textAlign="end">
                        <Button
                            variant="outlined"
                            onClick={() =>
                                onSettingsChange(prev =>
                                    updateTrigger(wizardMode, prev, {
                                        ...triggerFormSettingsInitialState
                                    })
                                )
                            }
                        >
                            Clear trigger settings
                        </Button>
                    </Box>

                </SettingsSection>
            </SettingsSectionBase>

            {!wizardMode && audience === FORM_AUDIENCE_TYPE.externalUsers && (
                <>
                    <Divider sx={{my: 5}}/>
                    <SettingsSectionBase
                        header="External User Recipients"
                        headerIcon={<PublicOutlinedIcon fontSize="small"/>}
                        subHeader={
                            externalRecipientsReadOnly
                                ? 'Recipients are taken from the selected email field above. Clear "Email from field" to enter addresses here.'
                                : 'Add email addresses for recipients without an account or linked profile.'
                        }
                    >
                        <SettingsSection>
                            <EmailsInput
                                values={externalEmails.filter(x => !x.isDeleted)}
                                onChange={emails =>
                                    onSettingsChange(prev => ({
                                        ...prev,
                                        externalUserEmailAssignments: mergeArraysByKey(
                                            emails,
                                            originalSettings.externalUserEmailAssignments,
                                            'email'
                                        )
                                    }))
                                }
                                onBlur={email =>
                                    onSettingsChange(prev => ({
                                        ...prev,
                                        externalUserEmailAssignments: mergeArraysByKey(
                                            [...externalEmails, {email}],
                                            originalSettings.externalUserEmailAssignments,
                                            'email'
                                        )
                                    }))
                                }
                                disabled={externalRecipientsReadOnly}
                            />
                        </SettingsSection>
                    </SettingsSectionBase>
                </>
            )}
        </>
    );
};

export default TriggerFormSettings;
